import { useState } from "react";
import { Mail, MapPin, Clock } from "lucide-react";
import { PageHero } from "../components/Shared";
import { Reveal } from "../components/Reveal";
import { useSubmit } from "../lib/api";
import { useTitle } from "../lib/useTitle";

export default function Contact() {
  useTitle("Contact | Bee Courageous @ Garden Grub");
  const { loading, submit } = useSubmit("/contact");
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    const res = await submit({ name: form.name, email: form.email, subject: form.subject || undefined, message: form.message });
    if (res?.success) setForm({ name: "", email: "", subject: "", message: "" });
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        lines={["We would love", "to hear from you."]}
        lede="Questions about the program, volunteering, giving, or partnerships — send a note and a caring member of our team will respond."
      />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-5">
            {[
              { icon: Mail, title: "Email", text: "Reach us anytime through the form — we typically reply within two business days." },
              { icon: MapPin, title: "Visit", text: "Join a community dinner or garden workday to experience the hive in person. See Services & Events for rhythms." },
              { icon: Clock, title: "Gathering Times", text: "Community dinners on third Wednesdays, with more gatherings announced by email." },
            ].map((c, i) => (
              <Reveal key={c.title} delay={0.08 * i}>
                <div className="card-soft flex items-start gap-5" data-testid={`contact-info-${i}`}>
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-wing-soft text-wing">
                    <c.icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-charcoal">{c.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-charcoal/70">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="lg:col-span-7">
            <form onSubmit={onSubmit} className="card-soft !border-wing/20" data-testid="contact-form">
              <h2 className="font-serif text-2xl font-semibold text-charcoal sm:text-3xl">Send us a message</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="label-field">Your name</label>
                  <input id="contact-name" required className="input-field" value={form.name} onChange={set("name")} data-testid="contact-name-input" />
                </div>
                <div>
                  <label htmlFor="contact-email" className="label-field">Your email</label>
                  <input id="contact-email" type="email" required className="input-field" value={form.email} onChange={set("email")} data-testid="contact-email-input" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-subject" className="label-field">Subject (optional)</label>
                  <input id="contact-subject" className="input-field" value={form.subject} onChange={set("subject")} data-testid="contact-subject-input" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="label-field">Your message</label>
                  <textarea id="contact-message" rows={5} required className="input-field" value={form.message} onChange={set("message")} data-testid="contact-message-input" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary mt-6 w-full disabled:opacity-60" data-testid="contact-submit-btn">
                {loading ? "Sending…" : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
